import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createPageUrl } from '@/utils';
import { LIVE_DEMOS, ARCHIVE_PROJECTS } from './showcaseData';

function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-950/80 border border-orange-500/40 text-[10px] font-bold text-orange-400 uppercase tracking-widest">
      <span className="inline-flex rounded-full w-1.5 h-1.5 bg-orange-400" />
      Live
    </span>
  );
}

function DemoCard({ demo, index, onPreview }) {
  return (
    <div
     
     
     
     
      className="group relative rounded-2xl overflow-hidden bg-zinc-900 border border-white/5 hover:border-white/15 transition-colors duration-300 shadow-2xl cursor-pointer"
      onClick={() => onPreview(demo)}
    >
      {/* Screenshot */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={demo.thumb}
          alt={`${demo.title} — ${demo.tagline}`}
          loading="lazy"
          className="w-full h-full object-cover object-top transform group-hover:scale-[1.04] transition-transform [transition-duration:900ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]"
        />
        {/* Hover veil + Preview CTA */}
        <div className="absolute inset-0 bg-zinc-950/0 group-hover:bg-zinc-950/55 transition-colors duration-400" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <span className="inline-flex items-center bg-white text-zinc-950 font-extrabold text-sm px-5 py-3 rounded-xl transform translate-y-3 group-hover:translate-y-0 transition-transform duration-300 hover:bg-orange-500 hover:text-white">
            Preview
          </span>
        </div>
        <div className="absolute top-3 left-3"><LiveBadge /></div>
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white bg-zinc-950/80 border border-white/15">
          {demo.categoryLabel}
        </div>
      </div>

      {/* Info bar */}
      <div className="p-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-white font-bold text-lg tracking-tight truncate">{demo.title}</h3>
          <p className="text-zinc-500 text-sm mt-0.5 truncate">{demo.tagline}</p>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ demo, onClose }) {
  const [frameReady, setFrameReady] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
     
     
     
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-8 bg-zinc-950/90"
      onClick={onClose}
    >
      <div
       
       
       
       
        className="relative w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-3xl bg-zinc-900 border border-white/10 shadow-[0_40px_120px_rgba(0,0,0,0.7)] grid lg:grid-cols-[1.5fr_1fr]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Live frame */}
        <div className="relative bg-zinc-950 hidden lg:block">
          {!frameReady && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-500">
              <div className="w-8 h-8 rounded-full border-2 border-zinc-700 border-t-orange-500 animate-spin" />
              <span className="text-xs font-semibold uppercase tracking-widest">Booting live preview…</span>
            </div>
          )}
          <iframe
            title={`${demo.title} live preview`}
            src={demo.url}
            onLoad={() => setFrameReady(true)}
            className={`w-full h-full min-h-[560px] border-0 transition-opacity duration-500 ${frameReady ? 'opacity-100' : 'opacity-0'}`}
          />
          <div className="absolute top-4 left-4"><LiveBadge /></div>
        </div>

        {/* Mobile screenshot fallback */}
        <div className="lg:hidden relative aspect-[16/10] overflow-hidden">
          <img src={demo.thumb} alt={demo.title} className="w-full h-full object-cover object-top" />
          <div className="absolute top-3 left-3"><LiveBadge /></div>
        </div>

        {/* Details */}
        <div className="p-6 md:p-8 overflow-y-auto max-h-[90vh]">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <span className="inline-block px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest text-orange-400 bg-orange-500/10 border border-orange-500/20 mb-3">
                {demo.categoryLabel}
              </span>
              <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight">{demo.title}</h3>
              <p className="text-zinc-400 mt-1">{demo.tagline}</p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close preview"
              className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-zinc-400 text-[15px] leading-relaxed mb-6">{demo.description}</p>

          {/* The prompt */}
          <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-4 mb-6">
            <div className="text-orange-400 text-[11px] font-bold uppercase tracking-widest mb-2">
              The prompt that built it
            </div>
            <p className="text-zinc-300 text-sm italic leading-relaxed">“{demo.prompt}”</p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 gap-2.5 mb-6">
            {demo.features.map((f) => (
              <div key={f} className="flex items-center gap-2 text-[13px] text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5">
                <span className="text-orange-400 shrink-0">✓</span>
                <span className="truncate">{f}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-5 text-xs text-zinc-500 mb-7">
            <span>Generated in {demo.buildTime}</span>
            <span>One prompt, zero code</span>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => window.open(demo.url, '_blank', 'noopener')}
              className="flex-1 bg-white text-zinc-950 hover:bg-orange-500 hover:text-white font-extrabold rounded-xl h-12 text-sm transition-all"
            >
              Open Live Site ↗
            </Button>
            <Link to={createPageUrl('AIBuilder')} className="flex-1">
              <Button
                variant="outline"
                className="w-full border-white/15 bg-white/5 text-white hover:bg-white/10 font-bold rounded-xl h-12 text-sm"
              >
                Build yours →
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShowcaseSection({ extended = false }) {
  const [active, setActive] = useState(null);

  return (
    <section id="showcase" className="py-24 md:py-32 bg-zinc-950 relative border-t border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div
         
         
         
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-[1.1]">
            One prompt. <span className="text-orange-500">Real, working products.</span>
          </h2>
          <p className="text-zinc-400 mt-6 text-lg font-light leading-relaxed">
            Every site below is live — generated by BuildersLab from a single sentence, not a screenshot or a mockup.
            Hover a card, preview the details, then open it and click around for yourself.
          </p>
        </div>

        {/* Live demo grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {LIVE_DEMOS.map((demo, i) => (
            <DemoCard key={demo.slug} demo={demo} index={i} onPreview={setActive} />
          ))}
        </div>

        {/* Home variant: CTA to full showcase */}
        {!extended && (
          <div
           
           
           
            className="flex justify-center mt-16"
          >
            <Link to={createPageUrl('Portfolio')}>
              <Button
                size="lg"
                className="bg-white text-zinc-950 hover:bg-zinc-200 font-extrabold rounded-xl h-14 px-8 text-sm hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] transition-all group"
              >
                Explore the full showcase →
              </Button>
            </Link>
          </div>
        )}

        {/* Portfolio variant: archive of earlier work */}
        {extended && (
          <div className="mt-24">
            <div
             
             
             
              className="flex items-end justify-between gap-6 mb-10"
            >
              <div>
                <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight">From the archive</h3>
                <p className="text-zinc-500 mt-2">Earlier client work and concepts crafted on the platform.</p>
              </div>
              <span className="text-xs font-bold text-zinc-600 uppercase tracking-widest shrink-0">
                {ARCHIVE_PROJECTS.length} projects
              </span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {ARCHIVE_PROJECTS.map((p, i) => (
                <div
                  key={p.title}
                 
                 
                 
                 
                  className="group relative rounded-2xl overflow-hidden aspect-[4/3] bg-zinc-900 border border-white/5 shadow-xl"
                >
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform [transition-duration:800ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/30 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between gap-3">
                    <h4 className="text-white font-bold tracking-tight drop-shadow-md">{p.title}</h4>
                    <span className="px-2.5 py-1 rounded-lg bg-zinc-950/80 border border-white/15 text-[10px] font-bold text-zinc-200 uppercase tracking-widest shrink-0">
                      {p.categoryLabel}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Preview modal */}
      {active && <PreviewModal demo={active} onClose={() => setActive(null)} />}
    </section>
  );
}
